import { useTranslation } from "react-i18next";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import OrdersService from "../../../api/orderHistoryService";
import useBreadcrumbsStore from "../../../stores/breadcrumbStore";
import { ConstantRoutes } from "../../../core/constantRoutes";
import { TableCell, TableHeader } from "../../table/tableCellHeader";
import useOfflineServiceStore from "../../../stores/admin/offlineServiceStore";
import OrderStatus from "../../common/orderStatus/orderStatus";
import TablePlaceholder from "../../skeleton/tablePlaceholder";
import { PackagingArrowDown } from "react-huge-icons/outline";
import AdminReportsService from "../../../api/admin/adminReportsService";

interface AdminTableServiceMainProps {
  label: string;
  path: string;
}

const AdminTableServiceMain: React.FC<AdminTableServiceMainProps> = ({
  label,
  path,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { addBreadcrumb, clearBreadcrumbs } = useBreadcrumbsStore();

  const {
    orders,
    fetchOrders,
    clearOrders,
    page,
    pageSize,
    totalPages,
    totalCount,
    setPage,
    setPageSize,
    email,
    setEmail,
    status,
    setStatus,
    uploadedFrom,
    setUploadedFrom,
    uploadedTo,
    setUploadedTo,
    resetFilters,
    software,
    setSoftware
  } = useOfflineServiceStore();

  const [isLoaded, setIsLoaded] = useState(false);
  const [inWork, setInWork] = useState(false);

  // Статусы для селекта
  const statuses = [
    { value: "UnderReview", label: "На рассмотрении" },
    { value: "Processing", label: "Обрабатывается" },
    { value: "RevisionToProcess", label: " На доработке" },
    { value: "Rejected", label: "Отклонен" },
    { value: "Completed", label: "Выполнен" }
  ];

  // Вызов при изменении фильтров или страницы
  const loadOrders = () => {
    fetchOrders(path).then(() => setIsLoaded(true));
  };

  useEffect(() => {
    clearOrders();
    loadOrders();

    clearBreadcrumbs();
    addBreadcrumb({
      label,
      path,
    });
  }, [page, pageSize, email, status, software, uploadedFrom, uploadedTo]);

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const handlePageSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setPageSize(Number(e.target.value));
  };

  function workWithFile(id: any): void {
    navigate(`/admin-tech/${path}/work/${id}`);
  }

  const downloadExcel = () => {
    setInWork(true);
    try {
      const reportsService = new AdminReportsService();
      reportsService.downloadFile(path, "report.xlsx");
    } finally {
      setInWork(false);
    }
  };

  return (
    <>
      {/* Фильтры */}
      <div className="p-4 bg-secondary rounded-xl shadow mb-6 grid grid-cols-1 md:grid-cols-7 gap-4">
        <div>
          <label className="block text-sm font-medium ">Email</label>
          <input
            type="text"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="user@example.com"
            className="mt-1 block w-full border border-gray-300 bg-secondary rounded-md shadow-sm py-2 px-3"
          />
        </div>

        <div>
          <label className="block text-sm font-medium ">Status</label>
          <select
            value={status || ""}
            onChange={(e) => setStatus(e.target.value || null)}
            className="mt-1 block w-full border border-gray-300 bg-secondary rounded-md shadow-sm py-2 px-3"
          >
            <option value="">All</option>
            {statuses.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium ">Software</label>
          <input
            type="text"
            value={software}
            onChange={(e) => setSoftware(e.target.value)}
            placeholder="SW0123456"
            className="mt-1 block w-full border border-gray-300 bg-secondary rounded-md shadow-sm py-2 px-3"
          />
        </div>      

        <div className="flex items-end">
          <button
            onClick={loadOrders}
            className="w-full bg-primary text-white  py-2 px-4 rounded hover:animate-pulse"
          >
            {t("Apply")}
          </button>
        </div>

        <div className="flex items-end">
          <button
            onClick={resetFilters}
            className="w-full bg-textsecondary text-white py-2 px-4 rounded hover:animate-pulse"
          >
            {t("Reset")}
          </button>
        </div>

        <div className="flex items-end">
          <button
            onClick={downloadExcel}
            className="w-full bg-careful text-white py-2 px-4 rounded hover:animate-pulse"
          >
            <div className="flex justify-center items-center">
              <PackagingArrowDown />
              {t("Export")}
            </div>
          </button>
        </div>
      </div>

      {/* Таблица */}
      <div className="flex-1 flex flex-col">
        {!isLoaded && <TablePlaceholder />}

        {isLoaded && (
          <>
            <div className="rounded-xl overflow-hidden border border-tableline max-md:hidden">
              <table className="w-full">
                <thead className="bg-tablebg2">
                  <tr>
                    <TableHeader additionalClassNames="w-20">#</TableHeader>
                    <TableHeader additionalClassNames="w-20">Email</TableHeader>
                    <TableHeader additionalClassNames="w-20">
                      Series
                    </TableHeader>
                    <TableHeader additionalClassNames="w-52">Ecu</TableHeader>
                    <TableHeader additionalClassNames="w-20">
                      Engine
                    </TableHeader>
                    <TableHeader additionalClassNames="w-52">
                      Software
                    </TableHeader>
                    <TableHeader additionalClassNames="w-52">
                      Options
                    </TableHeader>
                    <TableHeader additionalClassNames="w-32">
                      Uploaded
                    </TableHeader>
                    <TableHeader additionalClassNames="w-32">
                      Status
                    </TableHeader>
                    <TableHeader additionalClassNames="w-40">
                      Action
                    </TableHeader>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((order: any, index: number) => (
                    <tr
                      className={index % 2 === 0 ? "bg-tablebg" : "bg-tablebg2"}
                      key={index}
                    >
                      <TableCell>
                        <div className="flex flex-nowrap">
                          {order.isNewMessages && (
                            <div className="w-2 h-2 bg-primary rounded-full mt-2 -ml-2"></div>
                          )}
                          <div>#{order.id}</div>
                        </div>
                      </TableCell>
                      <TableCell>{order.email}</TableCell>
                      <TableCell>{order.series}</TableCell>
                      <TableCell>{order.ecu}</TableCell>
                      <TableCell>{order.engine}</TableCell>
                      <TableCell>{order.software}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap">
                          {order.options !== undefined &&
                            order.options.map(
                              (option: any, subindex: number) => (
                                <p className="py-1 px-2 m-1 text-primary bg-primary bg-opacity-30 rounded-xl">
                                  {option}
                                </p>
                              )
                            )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col">
                          <span>
                            {new Date(order.uploaded).toLocaleDateString(
                              "ru-RU"
                            )}
                          </span>
                          <span className="text-gray-500 text-sm">
                            {new Date(order.uploaded).toLocaleTimeString(
                              "ru-RU",
                              {
                                hour: "2-digit",
                                minute: "2-digit",
                              }
                            )}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <OrderStatus status={order.status} />
                      </TableCell>
                      <TableCell>
                        <div className="flex-1">
                          <button
                            className="flex items-center justify-center border-primary 
                            border border-solid rounded-lg px-3 cursor-pointer bg-primary min-h-min w-38 py-2"
                            onClick={() => workWithFile(order.id)}
                          >
                            {t("Work with file")}
                          </button>
                        </div>
                      </TableCell>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="md:hidden">
              {orders.map((order: any, index: number) => (
                <div
                  className="rounded-xl overflow-hidden border border-tableline bg-tablebg my-4 p-4
                  flex flex-wrap"
                >
                  <div className="p-2 w-1/2">
                    <p className="text-textsecondary">№</p>
                    <p>№ {order.id}</p>
                  </div>
                  <div className="p-2 w-1/2">
                    <p className="text-textsecondary">Email</p>
                    <p>{order.email}</p>
                  </div>

                  <div className="p-2 w-1/2">
                    <p className="text-textsecondary">Series</p>
                    <p>{order.series}</p>
                  </div>
                  <div className="p-2 w-1/2">
                    <p className="text-textsecondary">ECU</p>
                    <p>{order.ecu}</p>
                  </div>
                  <div className="p-2 w-1/2">
                    <p className="text-textsecondary">Engine</p>
                    <p>{order.engine}</p>
                  </div>
                  <div className="p-2 w-full">
                    <p className="text-textsecondary">Software</p>
                    <p>{order.software}</p>
                  </div>
                  <div className="p-2 w-full">
                    <p className="text-textsecondary">Options</p>
                    <div className="flex flex-wrap">
                      {order.options !== undefined &&
                        order.options.map((option: any, subindex: number) => (
                          <p className="py-1 px-2 m-1 text-primary bg-primary bg-opacity-30 rounded-xl">
                            {option}
                          </p>
                        ))}
                    </div>
                  </div>
                  <div className="p-2 w-1/2">
                    <p className="text-textsecondary">Uploaded</p>
                    <div className="flex flex-col">
                      <span>
                        {new Date(order.uploaded).toLocaleDateString("ru-RU")}
                      </span>
                      <span className="text-gray-500 text-sm">
                        {new Date(order.uploaded).toLocaleTimeString("ru-RU", {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </span>
                    </div>
                  </div>
                  <div className="p-2 w-1/2">
                    <p className="text-textsecondary">Status</p>
                    <p>
                      {" "}
                      <OrderStatus status={order.status} />
                    </p>
                  </div>
                  <div className="flex w-full justify-center border-t border-tableline pt-4">
                    <button
                      className="flex items-center justify-center border-primary 
                            border border-solid rounded-lg px-3 cursor-pointer bg-primary h-10 w-11/12 my-2 mx-2"
                      onClick={() => workWithFile(order.id)}
                    >
                      {t("Work with file")}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Пагинация */}
        {isLoaded && orders.length > 0 && (
          <div className="flex flex-col sm:flex-row justify-between items-center mt-6 space-y-4 sm:space-y-0">
            <div className="flex items-center space-x-2">
              <span>{t("Page")}</span>
              <select
                value={pageSize}
                onChange={handlePageSizeChange}
                className="border border-gray-300 bg-secondary rounded px-2 py-1"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span>{t("per page")}</span>
            </div>

            <div className="flex items-center space-x-1">
              <button
                onClick={() => handlePageChange(page - 1)}
                disabled={page <= 1}
                className="px-3 py-1 border rounded disabled:opacity-50"
              >
                ←
              </button>

              {[...Array(totalPages)].map((_, i) => {
                const pageNum = i + 1;
                if (
                  pageNum === 1 ||
                  pageNum === totalPages ||
                  (pageNum >= page - 2 && pageNum <= page + 2)
                ) {
                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`px-3 py-1 border rounded ${
                        pageNum === page ? "bg-primary text-white" : ""
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                } else if (pageNum === page - 3 || pageNum === page + 3) {
                  return (
                    <span key={pageNum} className="px-2">
                      ...
                    </span>
                  );
                }
                return null;
              })}

              <button
                onClick={() => handlePageChange(page + 1)}
                disabled={page >= totalPages}
                className="px-3 py-1 border rounded disabled:opacity-50"
              >
                →
              </button>
            </div>

            <div className="text-sm text-gray-500">
              {t("Showing")} {(page - 1) * pageSize + 1} -{" "}
              {Math.min(page * pageSize, totalCount)} {t("of")} {totalCount}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default AdminTableServiceMain;
